import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useQuery } from 'react-query';

import SelectMulti from 'app/components/Select/SelectMulti';
import { QUERIES } from 'app/components/Table/core/constants';
import Typography from 'app/components/Typography';

import {
  CUSTOM_OPTIONS,
  DEFAULT_RACK_FILTER,
  SELECTED_CUSTOM,
  SELECTED_WARD,
  WARD_OPTION,
} from 'app/modules/inventory/racks/core/constants';
import { getRacks2 } from 'app/modules/inventory/racks/core/requests';
import { RackFilterParams } from 'app/modules/inventory/racks/core/type';
import { Body, Header, Main } from 'app/modules/inventory/stock-run/components/StockRunFilter';
import { useChangeParams } from 'app/modules/query-state/hooks';
import { useQueryParamStore } from 'app/modules/query-state/store';
import { Ward } from 'app/modules/system-settings/locations/wards/core/type';

import isEqual from 'lodash/isEqual';

import { useOrganizationStore } from 'theme/layout/components/aside/store';

interface RackFilterProps {
  wards: Ward[];
  openFilter: boolean;
  setIsOpenFilter: (openFilter: boolean) => void;
}

const RackFilter: React.FC<RackFilterProps> = ({ wards, openFilter, setIsOpenFilter }) => {
  const wardFiltered = useMemo(() => wards.filter(({ id }) => id !== 0), [wards]);
  const organization = useOrganizationStore((state) => state.state.organization);
  const onChangeParams = useChangeParams().onChangeParams;
  const curParams = useQueryParamStore((s) => s.state.curParams);

  const [filter, setFilter] = useState<RackFilterParams>(DEFAULT_RACK_FILTER);
  const [menuIsOpen, setMenuIsOpen] = useState<{
    ward: boolean;
    is_custom: boolean;
  }>({
    ward: false,
    is_custom: false,
  });

  useEffect(() => {
    if (!openFilter || isEqual(curParams, filter)) return;
    setFilter({
      wardIds: Array.isArray(curParams.wardIds) ? curParams.wardIds : [],
      is_custom:
        curParams.is_custom !== undefined ? String(curParams.is_custom) === 'true' : undefined,
    });
  }, [curParams, openFilter]);

  const { data: racks, isFetching: isFetchingRacks } = useQuery({
    queryKey: [QUERIES.RACK_LIST],
    queryFn: () => getRacks2(`page=1&limit=999&orgId=${organization?.id}`),
    select: (data) => data.data?.items,
  });

  const handleCloseFilter = () => setIsOpenFilter(false);

  const handleApplyFilter = () => {
    onChangeParams({
      ...filter,
      page: 1,
    });
    handleCloseFilter();
  };

  const handleClearAll = () => {
    setFilter(DEFAULT_RACK_FILTER);
    onChangeParams(DEFAULT_RACK_FILTER);
    handleCloseFilter();
  };

  const handleSelectChange = useCallback(
    (key: keyof RackFilterParams) => (newValue: { value: string }[]) => {
      setFilter((prev) => ({
        ...prev,
        [key]: newValue.map((el) => String(el.value)),
      }));
      setMenuIsOpen((prev) => ({
        ...prev,
        [key === 'wardIds' ? 'ward' : key]: true,
      }));
    },
    [],
  );

  const handleCustomChange = useCallback((newValue: { value: string }) => {
    if (!newValue) {
      setFilter((prev) => ({
        ...prev,
        is_custom: undefined,
      }));
      return;
    }

    const value = newValue.value === 'true' ? true : false;
    setFilter((prev) => ({
      ...prev,
      is_custom: value,
    }));
    setMenuIsOpen((prev) => ({
      ...prev,
      is_custom: false,
    }));
  }, []);

  const handleOpenMenu = (key: keyof typeof menuIsOpen) => () =>
    setMenuIsOpen({ ...menuIsOpen, [key]: true });

  const handleCloseMenu = (key: keyof typeof menuIsOpen) => () =>
    setMenuIsOpen({ ...menuIsOpen, [key]: false });

  return (
    <Main>
      <Header>
        <Typography variant="heading-6">Filter</Typography>
        <Typography
          className="clear_all cursor-pointer"
          variant="body-5"
          onClick={handleClearAll}
          color="primary"
        >
          Clear all
        </Typography>
      </Header>
      <Body>
        <Typography variant="body-7" color="gray-700" className="mb-2">
          Location
        </Typography>
        <SelectMulti
          options={WARD_OPTION(wardFiltered) ?? []}
          selectedOption={SELECTED_WARD(filter.wardIds ?? [], wardFiltered)}
          handleChange={handleSelectChange('wardIds')}
          placeholder="Select location to filter"
          menuIsOpen={menuIsOpen.ward}
          onMenuClose={handleCloseMenu('ward')}
          openMenuOnClick={handleOpenMenu('ward')}
        />

        <Typography variant="body-7" color="gray-700" className="mb-2 mt-3">
          Type
        </Typography>
        <SelectMulti
          options={CUSTOM_OPTIONS}
          isMulti={false}
          selectedOption={SELECTED_CUSTOM(filter.is_custom)}
          handleChange={handleCustomChange}
          placeholder="Select type to filter"
          menuIsOpen={menuIsOpen.is_custom}
          onMenuClose={handleCloseMenu('is_custom')}
          openMenuOnClick={handleOpenMenu('is_custom')}
        />

        <div className="d-flex justify-content-end mt-4">
          <Button variant="primary" onClick={handleApplyFilter}>
            Apply
          </Button>
        </div>
      </Body>
    </Main>
  );
};

export default RackFilter;
